import React, { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import { debounce } from 'lodash';
import { DeleteOutlined, LoginOutlined, MailOutlined, QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Flex, Form, Input, Popconfirm, Row, Select, Table } from 'antd';
import { Alert, AlertDescription, AlertTitle, Badge, Button as DsButton } from '@foodamigos/ui';
import { useImmer } from 'use-immer';
import CollapsibleForm from '../../../components/form/general/CollapsibleForm';
import ChangeOwnerDialog from '../../../components/team/ChangeOwnerDialog';
import useForm from '../../../hooks/utility/useForm';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import useApiClient from '../../../hooks/api/useApiClient';
import useHelpers from '../../../hooks/context/useHelpers';
import usePermissions from '../../../hooks/auth/usePermissions';
import usePlgOnboarding from '../../../hooks/auth/usePlgOnboarding';
import useAnalytics from '../../../hooks/analytics/useAnalytics';
import { OwnerChangeEvent } from '../../../services/analytics/ownerChangeEvents';
import { NewOwnerChoice, newOwnerKind, ownerCandidates } from '../../../services/team/changeOwner';
import { ManagerRole } from '../../../services/permissions/PermissionResolver';
import { firstApiError } from '../../../services/helpers/ApiErrors';
import {
  buildCreateManagerPayload,
  getAssignableRoles,
  isOwnerRole,
  isProtectedManager,
} from '../../../services/team/businessManagerRoles';
import {
  useChangeCompanyOwnerMutation,
  useCreateBusinessManagersMutation,
  useDeleteBusinessManagersMutation,
  useGetBusinessManagersQuery,
  useUpdateBusinessManagersMutation,
} from '../../../store/api-slices';

const DEFAULT_PER_PAGE = 15;

/** The change-owner dialog picks from the whole team, not the page on screen. */
const TEAM_PER_PAGE = 100;

/** Role tag of a row: Owner amber, Operator blue, Employee neutral (design `change-owner`). */
function roleBadge(manager) {
  if (manager.is_owner) {
    return { variant: 'warning', key: 'tags.owner' };
  }

  if (manager.roles?.includes(ManagerRole.StoreOperator)) {
    return { variant: 'info', key: 'tags.operator' };
  }

  if (manager.roles?.includes(ManagerRole.StoreEmployee)) {
    return { variant: 'neutral', key: 'tags.employee' };
  }

  return null;
}

export default function BusinessManagersView() {
  const { ManagersManager, MagicLinksManager } = useApiClient();
  const { isAdmin } = usePermissions();
  const canProvisionOwner = usePlgOnboarding();
  // Partner admins and super admins of a self-serve partner; the backend refuses everyone else.
  const canChangeOwner = isAdmin && canProvisionOwner;
  const { track } = useAnalytics();
  const groupName = useSelector((state) => state.currentFranchise?.franchise?.name);
  const franchiseId = useSelector((state) => state.currentFranchise?.franchise?.id);
  const currentCompanyId = useSelector((state) => state.currentCompany.company?.id);
  const companies = useSelector(
    (state) => state.currentCompany.company.franchise?.companies ?? state.currentFranchise.franchise?.companies,
  );
  const companiesByUuid = useMemo(() => collect(companies).keyBy('uuid'), [companies]);
  const companiesById = useMemo(() => collect(companies).keyBy('id'), [companies]);
  const [changeOwnerTarget, setChangeOwnerTarget] = useState(null);
  // Loaded up front for whoever can change the owner, so the dialog opens with the team in it.
  const { currentData: teamResponse, isFetching: loadingTeam } = useGetBusinessManagersQuery(
    { apiManager: ManagersManager, franchiseId, page: 1, per_page: TEAM_PER_PAGE },
    { skip: !canChangeOwner },
  );
  const [changeOwnerMtn] = useChangeCompanyOwnerMutation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const { currentData: response, isFetching: isLoading } = useGetBusinessManagersQuery({
    apiManager: ManagersManager,
    franchiseId,
    page,
    per_page: perPage,
    ...(search ? { search } : {}),
  });
  // Backend returns a Laravel paginator: { data: [...], total, current_page, ... }.
  // Guard against unexpected shapes so the Table never receives a non-array.
  const managers = Array.isArray(response?.data) ? response.data : [];
  const total = response?.total ?? managers.length;
  const ownedCompanyIds = useMemo(() => response?.owned_company_ids ?? [], [response]);
  const ownerlessCompanyIds = response?.ownerless_company_ids ?? [];
  const [createManagerMtn, { isLoading: creating }] = useCreateBusinessManagersMutation();
  const [updateManagerMtn, { isLoading: updating }] = useUpdateBusinessManagersMutation();
  const [deleteManagerMtn, { isLoading: deleting }] = useDeleteBusinessManagersMutation();
  const [editedManagers, setEditedManagers] = useImmer({});
  const [loggingInManagerId, setLoggingInManagerId] = useState(null);
  const [resendingInviteManagerId, setResendingInviteManagerId] = useState(null);
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Team:Managers:ManagersTab' });
  const { formatDateTime } = useHelpers();
  const {
    data,
    update,
    reset: resetData,
  } = useForm({
    companies: [],
    first_name: null,
    last_name: null,
    email: null,
    role: null,
  });
  const { setFeedback, renderFeedbackLabel, resetFeedback } = useFeedbackLabel();
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 400),
    [],
  );
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);
  const createManager = () => {
    const payload = buildCreateManagerPayload(data);

    createManagerMtn({
      apiManager: ManagersManager,
      ...payload,
    })
      .unwrap()
      .then(() => {
        resetData();
        resetFeedback();
        toast.success(
          payload.as_owner
            ? t('toasts.owner_invited', { email: payload.email })
            : t('toasts.successfully_created_manager'),
        );
      })
      .catch((error) => {
        setFeedback({
          message: error?.message,
          type: 'error',
          errors: error?.errors,
        });

        toast.error(t('toasts.failed_to_create_manager'));
      });
  };

  const updateManager = (manager) => {
    setEditedManagers((draft) => {
      delete draft[manager.id];
    });
    updateManagerMtn({
      apiManager: ManagersManager,
      managerId: manager.id,
      companies: editedManagers[manager.id],
    })
      .unwrap()
      .then(() => toast.success(t('toasts.successfully_updated_manager')))
      .catch(() => toast.error(t('toasts.failed_to_update_manager')));
  };

  const logInAs = async (manager) => {
    setLoggingInManagerId(manager.id);
    // Scope the impersonated session to the currently selected company (falling
    // back to the franchise) so the magic link lands in the right tenant.
    const scope = {};
    if (currentCompanyId) {
      scope.company_id = currentCompanyId;
    } else if (franchiseId) {
      scope.franchise_id = franchiseId;
    }

    const { success, data: linkData } = await MagicLinksManager.create({
      app: 'dashboard',
      manager_id: manager.id,
      ...scope,
    });
    setLoggingInManagerId(null);

    if (!success) {
      return toast.error(t('toasts.failed_to_create_magic_link'));
    }

    return window.location.replace(linkData.url);
  };

  const resendOwnerInvite = async (manager) => {
    setResendingInviteManagerId(manager.id);

    try {
      const { success, status, data: response } = await ManagersManager.resendOwnerInvite(manager.id);

      if (success) {
        return toast.success(t('toasts.owner_invite_resent', { email: manager.email }));
      }

      if (status === 429 || response?.error_code === 'owner_invite_resend_cooling_down') {
        return toast.info(t('toasts.owner_invite_resend_cooling_down'));
      }

      return toast.error(response?.title ?? firstApiError(response) ?? t('toasts.failed_to_resend_owner_invite'));
    } catch {
      return toast.error(t('toasts.failed_to_resend_owner_invite'));
    } finally {
      setResendingInviteManagerId(null);
    }
  };

  /**
   * The owner is set on the legal entity, so the change covers every restaurant of it the
   * owner holds here: the dialog names them all.
   */
  const ownedRestaurants = (owner) => {
    const owned = (owner.owned_company_ids ?? []).map((id) => companiesById.get(id)).filter(Boolean);

    return owned.length ? { id: owned[0].id, name: owned.map((company) => company.name).join(', ') } : null;
  };

  const openChangeOwner = (restaurant, owner = null) => {
    if (!restaurant) {
      return;
    }

    setChangeOwnerTarget({ restaurant, owner });
    track(OwnerChangeEvent.Opened, { restaurant_id: restaurant.id });
  };

  const changeOwner = async (payload, { choice, previous, name, email }) => {
    const { restaurant, owner } = changeOwnerTarget;

    // Rejects with the response body, which the dialog reads.
    await changeOwnerMtn({ apiManager: ManagersManager, ...payload }).unwrap();

    track(OwnerChangeEvent.Confirmed, {
      restaurant_id: restaurant.id,
      new_owner: newOwnerKind(choice),
      previous_owner: owner ? previous : null,
    });

    if (choice === NewOwnerChoice.None) {
      toast.success(t('toasts.owner_removed', { name: owner?.name }));
    } else if (choice === NewOwnerChoice.New) {
      toast.success(t('toasts.owner_changed_new', { name, email }));
    } else {
      toast.success(t('toasts.owner_changed', { name }));
    }
  };

  const deleteManager = async (manager) => {
    deleteManagerMtn({
      apiManager: ManagersManager,
      managerId: manager.id,
    })
      .unwrap()
      .then(() => toast.success(t('toasts.manager_got_deleted')))
      .catch(() => toast.error(t('toasts.failed_to_delete_manager')));
  };

  const columns = [
    {
      title: t('fields.email'),
      key: 'email',
      dataIndex: 'email',
    },
    {
      title: t('fields.name'),
      key: 'name',
      dataIndex: 'name',
    },
    {
      title: t('fields.roles'),
      key: 'roles',
      render: (_, item) => {
        const badge = roleBadge(item);

        if (!badge) {
          // Store admins that aren't the linked owner keep "Admin".
          return collect(item.roles)
            .map((role) => t(`form.roles.options.${role}`))
            .implode(', ');
        }

        return (
          <Badge size="md" variant={badge.variant} data-testid={item.is_owner ? 'manager-owner-tag' : undefined}>
            {t(badge.key)}
          </Badge>
        );
      },
    },
    {
      dataIndex: 'company_ids',
      title: t('fields.companies'),
      key: 'company_ids',
      render: (companyUuids, row) => (
        <Select
          className="w-[300px]"
          mode="multiple"
          disabled={isProtectedManager(row)}
          value={editedManagers[row.id] ?? companyUuids?.map((companyUuid) => companiesByUuid.get(companyUuid)?.id)}
          options={companies.map((item) => ({ label: item.name, value: item.id }))}
          onChange={(value) => setEditedManagers((draft) => ({ ...draft, [row.id]: value }))}
        />
      ),
    },
    {
      title: t('fields.created_at'),
      key: 'created_at',
      render: (_, item) => formatDateTime(item.created_at),
    },
    {
      title: t('fields.actions'),
      key: 'actions',
      render: (_, item) => (
        <div className="space-x-3">
          {/* "Login as manager" is restricted to admins / super admins. */}
          {isAdmin && (
            <Button
              icon={<LoginOutlined />}
              title={t('buttons.login_as_manager')}
              loading={loggingInManagerId === item.id}
              onClick={() => logInAs(item)}
            />
          )}
          {item.is_owner && canProvisionOwner && (
            <Popconfirm
              icon={<QuestionCircleOutlined />}
              title={t('modals.resend_owner_invite.title', { email: item.email })}
              description={t('modals.resend_owner_invite.description')}
              okButtonProps={{
                className: 'text-primary',
                loading: resendingInviteManagerId === item.id,
              }}
              onConfirm={() => resendOwnerInvite(item)}
            >
              <Button
                icon={<MailOutlined />}
                title={t('buttons.resend_owner_invite')}
                loading={resendingInviteManagerId === item.id}
                data-testid="manager-resend-invite"
              />
            </Popconfirm>
          )}
          {editedManagers[item.id] && !isProtectedManager(item) && (
            <Popconfirm
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              title={t('modals.update_manager.title', { email: item.email })}
              okButtonProps={{
                className: 'text-primary',
                loading: updating,
              }}
              onConfirm={() => updateManager(item)}
            >
              <Button loading={updating} icon={<SaveOutlined />} />
            </Popconfirm>
          )}
          {item.is_owner && canChangeOwner && (
            <Button
              danger
              icon={<DeleteOutlined />}
              aria-label={t('buttons.change_owner', { name: item.name })}
              title={t('buttons.change_owner', { name: item.name })}
              data-testid="manager-change-owner"
              onClick={() => openChangeOwner(ownedRestaurants(item), item)}
            />
          )}
          {!isProtectedManager(item) && (
            <Popconfirm
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              title={t('modals.delete_manager.title', { email: item.email })}
              okButtonProps={{
                danger: true,
                loading: deleting,
              }}
              onConfirm={() => deleteManager(item)}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                aria-label={t('buttons.delete_named', { name: item.name })}
                data-testid="manager-delete"
              />
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];
  const roles = getAssignableRoles({ canProvisionOwner });
  const invitingOwner = isOwnerRole(data.role);
  const companyOptions = companies.map((item) => {
    // A restaurant has one owner; the backend refuses a second.
    const hasOwner = invitingOwner && ownedCompanyIds.includes(item.id);

    return {
      label: hasOwner ? `${item.name} (${t('form.companies.has_owner')})` : item.name,
      value: item.id,
      disabled: hasOwner,
    };
  });
  const changeRole = (value) => {
    // Owner takes a single restaurant, so switching role in either direction resets the selection.
    if (isOwnerRole(value) !== invitingOwner) {
      update('companies', []);
    }

    update('role', value);
  };

  return (
    <>
      <CollapsibleForm title={t('form.title')} className="mt-md">
        <Form layout="vertical" className="mt-md">
          {renderFeedbackLabel}
          <Row gutter={8}>
            <Col xs={24} md={12}>
              <Form.Item label={t('form.first_name.label')}>
                <Input
                  value={data.first_name ?? ''}
                  onChange={(e) => update('first_name', e.target.value)}
                  placeholder={t('form.first_name.placeholder')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('form.last_name.label')}>
                <Input
                  value={data.last_name ?? ''}
                  onChange={(e) => update('last_name', e.target.value)}
                  placeholder={t('form.last_name.placeholder')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={t('form.email.label')}>
            <Input
              value={data.email ?? ''}
              onChange={(e) => update('email', e.target.value)}
              placeholder={t('form.email.placeholder')}
              type="email"
            />
          </Form.Item>
          <Form.Item label={t('form.roles.label')}>
            <Select
              value={data.role}
              onChange={changeRole}
              data-testid="manager-role-select"
              options={roles.map((item) => ({
                label: isOwnerRole(item) ? t('form.roles.owner_option') : t(`form.roles.options.${item}`),
                value: item,
              }))}
              placeholder={t('form.roles.placeholder')}
            />
            {roles.map((role) => (
              <p className="mt-2 text-xs" key={role}>
                <Trans t={t} i18nKey={`form.roles.description.${role}`} components={{ strong: <strong /> }} />
              </p>
            ))}
          </Form.Item>
          <Form.Item label={t('form.companies.label')}>
            {invitingOwner ? (
              <Select
                allowClear
                value={data.companies[0]}
                onChange={(value) => update('companies', value ? [value] : [])}
                options={companyOptions}
                placeholder={t('form.companies.owner_placeholder')}
                data-testid="manager-owner-company-select"
              />
            ) : (
              <Select
                mode="multiple"
                allowClear
                value={data.companies}
                onChange={(value) => update('companies', value)}
                options={companyOptions}
                placeholder={t('form.companies.placeholder')}
              />
            )}
          </Form.Item>
        </Form>
        <Flex justify="end" className="mt-md">
          <Button type="primary" loading={creating} onClick={createManager}>
            {t('buttons.add_manager')}
          </Button>
        </Flex>
      </CollapsibleForm>
      {ownerlessCompanyIds
        .map((companyId) => companiesById.get(companyId))
        .filter(Boolean)
        .map((restaurant) => (
          <Alert
            key={restaurant.id}
            variant="warning"
            className="mt-md"
            data-testid="manager-no-owner-notice"
            action={
              canChangeOwner && (
                <DsButton size="sm" variant="secondary" onClick={() => openChangeOwner(restaurant)}>
                  {t('buttons.choose_owner')}
                </DsButton>
              )
            }
          >
            <AlertTitle>{t('no_owner.title', { restaurant: restaurant.name })}</AlertTitle>
            <AlertDescription>{t('no_owner.description')}</AlertDescription>
          </Alert>
        ))}
      <Flex className="mt-md mb-md">
        <Input.Search
          placeholder={t('search.placeholder')}
          className="max-w-[448px]"
          allowClear
          onChange={(e) => debouncedSetSearch(e.target.value?.trim() ?? '')}
        />
      </Flex>
      <Table
        columns={columns}
        dataSource={managers}
        loading={isLoading}
        rowKey={(item) => item.id}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          showSizeChanger: true,
        }}
        onChange={(pagination) => {
          setPage(pagination.current);
          setPerPage(pagination.pageSize);
        }}
        className="rounded-none shadow-card bg-white overflow-hidden"
      />
      {changeOwnerTarget && (
        <ChangeOwnerDialog
          open
          onOpenChange={(open) => !open && setChangeOwnerTarget(null)}
          restaurant={changeOwnerTarget.restaurant}
          owner={changeOwnerTarget.owner}
          candidates={ownerCandidates(teamResponse?.data, changeOwnerTarget.owner?.id)}
          loadingCandidates={loadingTeam && !teamResponse}
          groupName={groupName}
          onSubmit={changeOwner}
          onFailed={(errorCode) =>
            track(OwnerChangeEvent.Failed, { restaurant_id: changeOwnerTarget.restaurant.id, error_code: errorCode })
          }
        />
      )}
    </>
  );
}
